import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authService, profileService, organizationRepo, branchRepo, gsEnabled, USER_TYPE } from "../services";
import { seedAll } from "../mock/store";
import { getBusinessModel } from "../config/business-models";
import { CORE_MODULE_KEYS } from "../config/feature-modules";
import { resolveSubscription, effectiveLimit, lowestPlanWith } from "../config/plans";
import { storageCheck } from "../config/storage";
import { useI18n } from "../i18n";

const ORG_KEY = "levelup:current-org";
const BRANCH_KEY = "levelup:current-branch";

const AppContext = createContext(null);

/** ร้านที่บัญชีนี้เห็นได้ — ไม่มี org_id = ไม่เห็นร้านใดเลย (ยกเว้นโหมดเดโมในเครื่อง) */
const loadOrganizations = (myOrgId) => {
  if (myOrgId) return organizationRepo.get(null, myOrgId).then((row) => (row ? [row] : []));
  if (!gsEnabled()) return organizationRepo.all();
  return Promise.resolve([]);
};

export function AppProvider({ children }) {
  const { tx } = useI18n();
  const [profile, setProfile] = useState(authService.currentProfile());
  const [organizations, setOrganizations] = useState([]);
  const [orgId, setOrgId] = useState(() => localStorage.getItem(ORG_KEY) || "");
  const [branchId, setBranchId] = useState(() => localStorage.getItem(BRANCH_KEY) || "");
  const [branches, setBranches] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [ready, setReady] = useState(false);

  // โหลด seed + ร้านของผู้ใช้ที่ล็อกอินอยู่
  //
  // ทุกบัญชี (แอดมินและลูกค้า) ผูกกับร้านเดียวผ่าน profile.org_id — เห็นเฉพาะร้านตัวเอง
  // บัญชีที่ไม่มี org_id (บัญชีเก่าก่อนแยกร้าน / super_user ซึ่งใช้หน้า /super แยกต่างหาก)
  // ห้ามโหลดรายชื่อร้านทั้งระบบ — ได้ organizations ว่าง + แจ้งให้ติดต่อทีมงาน
  // ข้อยกเว้น: โหมดเดโม localStorage (ไม่ได้ตั้ง REACT_APP_GS_URL) มีแต่ข้อมูลตัวอย่างในเครื่อง จึงยังเห็นร้านตัวอย่างเหมือนเดิม
  useEffect(() => {
    seedAll(false);

    const myOrgId = profile?.org_id || "";

    loadOrganizations(myOrgId)
      .then((rows) => {
        setOrganizations(rows);
        setOrgId((current) => {
          const valid = rows.find((row) => row.id === current);
          const next = myOrgId || (valid ? current : rows[0]?.id || "");
          localStorage.setItem(ORG_KEY, next);
          return next;
        });
        // ล็อกอินแล้วแต่ไม่มีร้านให้ใช้ (ไม่ใช่ super_user ที่ใช้หน้า /super แยก) -> แจ้งให้ติดต่อทีมงาน
        if (profile && !rows.length && profile.user_type !== USER_TYPE.superUser) {
          toast(tx("บัญชีนี้ยังไม่ได้ผูกกับร้านใด กรุณาติดต่อทีมงาน", "This account is not linked to any shop — please contact support"), "error");
        }
      })
      .catch((err) => {
        // อย่าให้ค้างทั้งแอปถ้าโหลดข้อมูลร้านไม่สำเร็จ (เช่น Google Sheets ล่ม/ยังไม่เปิดสิทธิ์ Anyone)
        console.warn("โหลดข้อมูลร้านไม่สำเร็จ:", err.message);
        toast(tx("โหลดข้อมูลองค์กรไม่สำเร็จ ตรวจสอบการเชื่อมต่อ Google Sheets", "Failed to load organizations — check Google Sheets connection"), "error");
      })
      .finally(() => setReady(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.org_id]);

  // โหลดสาขาของ org ปัจจุบัน
  useEffect(() => {
    if (!orgId) return;
    branchRepo.all(orgId).then((rows) => {
      setBranches(rows);
      setBranchId((current) => {
        const valid = rows.find((row) => row.id === current);
        const next = valid ? current : "";
        localStorage.setItem(BRANCH_KEY, next);
        return next;
      });
    });
  }, [orgId]);

  // response ตอน login มีแค่ token — ดึงโปรไฟล์เต็มมาเติมชื่อ/อีเมลให้ topbar
  useEffect(() => {
    if (!profile || profile.fullname) return;
    let alive = true;
    profileService.get().then((full) => {
      if (alive && full) setProfile(full);
    });
    return () => {
      alive = false;
    };
  }, [profile]);

  // sync auth ข้ามแท็บ / หลัง login-logout
  useEffect(() => {
    const sync = () => setProfile(authService.currentProfile());
    window.addEventListener("storage", sync);
    window.addEventListener("crm-auth-change", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("crm-auth-change", sync);
    };
  }, []);

  const organization = useMemo(
    () => organizations.find((row) => row.id === orgId) || organizations[0] || null,
    [organizations, orgId]
  );

  const branch = useMemo(() => branches.find((row) => row.id === branchId) || null, [branches, branchId]);

  // สถานะแพ็กเกจของร้าน — ยึดวันหมดอายุจริง (ไม่เชื่อ field status ที่ค้างในชีต)
  const subscription = useMemo(() => resolveSubscription(organization), [organization]);

  // โมดูลที่ใช้งานได้จริง = (โมดูลหลัก + ที่เปิดไว้เอง) ∩ สิทธิ์ของแพ็กเกจที่จ่ายอยู่
  // ยังไม่ซื้อ/หมดอายุ = ใช้ได้เฉพาะชุดฟรี (subscription.modules เป็นชุดฟรีอยู่แล้ว)
  const enabledModules = useMemo(() => {
    const wanted = organization
      ? Array.from(new Set([...CORE_MODULE_KEYS, ...(organization.enabledModules || [])]))
      : CORE_MODULE_KEYS;
    return wanted.filter((key) => subscription.modules.includes(key));
  }, [organization, subscription]);

  const businessModel = useMemo(() => getBusinessModel(organization?.businessModel), [organization]);

  const switchOrg = useCallback((nextId) => {
    localStorage.setItem(ORG_KEY, nextId);
    localStorage.removeItem(BRANCH_KEY);
    setBranchId("");
    setOrgId(nextId);
  }, []);

  const switchBranch = useCallback((nextId) => {
    localStorage.setItem(BRANCH_KEY, nextId || "");
    setBranchId(nextId || "");
  }, []);

  const setModules = useCallback(
    async (modules) => {
      if (!organization) return;
      // เปิดได้เฉพาะโมดูลที่อยู่ในแพ็กเกจที่จ่ายอยู่ (กันแก้ผ่าน devtools แล้วปลดล็อกเอง)
      const next = Array.from(new Set([...CORE_MODULE_KEYS, ...modules])).filter((key) =>
        subscription.modules.includes(key)
      );
      await organizationRepo.update(null, organization.id, { enabledModules: next });
      setOrganizations((rows) => rows.map((row) => (row.id === organization.id ? { ...row, enabledModules: next } : row)));
    },
    [organization, subscription]
  );

  const refreshOrganizations = useCallback(async () => {
    const rows = await loadOrganizations(profile?.org_id || "");
    setOrganizations(rows);
  }, [profile?.org_id]);

  const toast = useCallback((message, tone = "") => {
    const id = Date.now() + Math.random();
    setToasts((rows) => [...rows, { id, message, tone }]);
    setTimeout(() => setToasts((rows) => rows.filter((row) => row.id !== id)), 3200);
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setProfile(null);
  }, []);

  const value = useMemo(
    () => ({
      ready,
      profile,
      setProfile,
      isLoggedIn: !!profile,
      isAdmin: profile?.user_type === USER_TYPE.admin,
      // customers row id ของผู้ใช้ฝั่งลูกค้า (ผูกตอนสมัครผ่านลิงก์ร้าน)
      customerId: profile?.customerId || "",
      organizations,
      organization,
      orgId: organization?.id || "",
      /** ล็อกอินอยู่แต่ไม่มีร้านผูกกับบัญชี (ใช้โชว์ข้อความให้ติดต่อทีมงาน) */
      noOrg: ready && !!profile && organizations.length === 0,
      branches,
      branch,
      branchId,
      enabledModules,
      businessModel,
      hasModule: (key) => !key || enabledModules.includes(key),

      // ------- แพ็กเกจ & โควตา -------
      subscription,
      planKey: subscription.planKey,
      plan: subscription.plan,
      planActive: subscription.isActive,
      /** ยังไม่ซื้อ / หมดอายุ — ใช้ชุดฟรี */
      planFree: subscription.isFree,
      planModules: subscription.modules,
      /** โมดูลนี้อยู่นอกแพ็กเกจ + ฟีเจอร์เสริมที่จ่ายอยู่หรือไม่ (ใช้โชว์กุญแจ + ชวนอัปเกรด) */
      moduleLocked: (key) => !!key && !subscription.modules.includes(key),
      /** แพ็กเกจต่ำสุดที่ปลดล็อกโมดูลนี้ */
      upgradeFor: (key) => lowestPlanWith(key),
      /** โควตาของแพ็กเกจ + ฟีเจอร์เสริม (null = ไม่จำกัด) */
      limitOf: (name) => effectiveLimit(subscription, name),
      /** เช็คก่อนสร้างข้อมูลใหม่ — { ok, limit, used } */
      checkQuota: (name, used = 0) => {
        const limit = effectiveLimit(subscription, name);
        return { ok: limit === null || used < limit, limit, used };
      },
      /** เช็คก่อนอัปโหลดไฟล์ — { ok, unlimited, limitBytes, usedBytes, afterBytes } (ที่ใช้อยู่ + ไฟล์ใหม่ ต้องไม่เกินโควตา storage GB) */
      checkStorage: (usedBytes = 0, addBytes = 0) => storageCheck(effectiveLimit(subscription, "storage"), usedBytes, addBytes),
      switchOrg,
      switchBranch,
      setModules,
      refreshOrganizations,
      toast,
      toasts,
      logout,
    }),
    [ready, profile, organizations, organization, branches, branch, branchId, enabledModules, businessModel, subscription, switchOrg, switchBranch, setModules, refreshOrganizations, toast, toasts, logout]
  );

  return (
    <AppContext.Provider value={value}>
      {children}
      <div className="toast-wrap">
        {toasts.map((item) => (
          <div key={item.id} className={`toast-crm ${item.tone ? `is-${item.tone}` : ""}`}>
            {item.message}
          </div>
        ))}
      </div>
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
